import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Flex } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { serialize } from 'object-to-formdata';
import { useMemo } from 'react';
import HeaderNavigation from 'components/HeaderNavigation';
import { FORM_MAX_WIDTH } from 'utils/constants';
import FooterButtons, { FOOTER_HEIGHT } from 'components/FooterButtons';
import FormBody from 'pages/Venues/Plans/Form/components/FormBody';
import { BODY_SCHEMA, DEFAULT_VALUES } from 'pages/Venues/Plans/Form/constants';
import ROUTES from 'app/routes';
import { API, APIRoutes } from 'api';
import { setFormErrors } from 'utils/api';
import ComponentPreview from 'components/PreviewWrapper';
import { SERIALIZER_OPTIONS } from 'app/constants';
import FormWrapper from 'components/FormWrapper';
import { useGetVenue } from 'api/venues';
import { convertKeysToSnakeCase } from 'utils/helpers';

function VenuePlanCreate() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const backPath = useMemo(
    () =>
      generatePath(ROUTES.venues.plans.base, {
        id,
      }),
    [id],
  );

  const methods = useForm<Plan>({
    mode: 'onChange',
    resolver: yupResolver(BODY_SCHEMA),
    defaultValues: DEFAULT_VALUES,
  });

  const { handleSubmit, setError } = methods;

  const { data: { data: venue } = {} } = useGetVenue(+id);

  const { mutate: createVenuePlan, isLoading } = useMutation({
    mutationKey: 'venuePlansMutation',
    mutationFn: (data: Plan) => {
      const snakePayload = convertKeysToSnakeCase(data);
      const formData = serialize(snakePayload, SERIALIZER_OPTIONS, new FormData(), 'plan');
      return API.post(APIRoutes.venues.plans.index(+id), formData);
    },
    onSuccess: () => {
      toast.success('Plan został pomyślnie utworzony!');
      navigate(backPath);
      void queryClient.invalidateQueries('venuePlans');
    },
    onError: (error) => {
      setFormErrors(setError, error);
      toast.error('Wystąpił problem podczas tworzenia planu');
    },
  });

  const onSubmit = handleSubmit((data: Plan) => createVenuePlan(data));

  return (
    <Box>
      <HeaderNavigation
        baseCrumb={{
          label: venue?.name ?? '',
          to: backPath,
        }}
        crumbs={[{ to: '', label: 'Dodaj plan' }]}
      />
      <FormWrapper {...methods}>
        <Flex gap={4} mb={4} alignItems="flex-start" pb={FOOTER_HEIGHT}>
          <Box w="100%" as="form" id="venue-plan-creator" onSubmit={onSubmit} maxW={FORM_MAX_WIDTH}>
            <FormBody isLoading={isLoading} />
          </Box>
          <ComponentPreview kind="plan" />
        </Flex>
      </FormWrapper>
      <FooterButtons isLoading={isLoading} isCreate formId="venue-plan-creator" onCancel={() => navigate(-2)} />
    </Box>
  );
}

export default VenuePlanCreate;
